import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFundedSummary } from '../../hooks/useFundedSummary';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { supabaseClient } from '../../utils/supabase';
import { toast as message } from 'sonner';
import { useGlobalMetadata } from '../../utils/providers';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import { useLocation } from 'react-router-dom';
import { useRecordNavigator, useRecordParam } from '../../hooks/useRecordNavigator';
import { OVERLAY_PARAM } from '../../utils/recordTargets';
import WorkspaceGrid from '../WorkspaceGrid/WorkspaceGrid';
import { ListLeaderboard } from '../ListLeaderboard/ListLeaderboard';
import CreateListRecordButton from '../CreateListRecordButton';
import LeadSourceImports, { LeadSourceEditButton } from './LeadSourceImports';
import { queryClient } from '../../utils/constants';
import { LivePicklistCell, MERCHANT_FEEDBACK_EDIT } from '../ReportPage/LivePicklistCell';
import { SHEET_DEAL_SELECT, sheetDealColumns, sheetDealFields, type SheetDealFields } from '../../utils/sheetDealColumns';
import FundedDashboard from '../admin/reporting/FundedDashboard';
import '../RepPage/RepPage.css';
import '../FunderPage/FunderPage.css';
import './LeadSourcePage.css';

export interface LeadRow {
  id: number;
  source: string;
  gross: number;
  fundedAmount: number;
  deals: number;
  perDeal: number;
  cost: number | null;
  revShare: number | null;
  realNet: number | null;
  costPct: number | null;
  netPct: number | null;
  /** REG-828: funded amount over funded deals. */
  averageFunded: number | null;
  /** REG-828: committed imported lead rows (Lead Imports F1); null before the import reports first refresh. */
  totalLeads: number | null;
  /** REG-828: distinct phones imported for the source (Lead Imports F2). */
  uniqueLeads: number | null;
  /** REG-828 / REG-712: the lead source's Active flag (inactive ones stay in history and reports). */
  active: boolean;
  hasData: boolean;
}

type Decision = 'funded' | 'approved' | 'declined' | 'no_response';

interface SubmissionRecord extends SheetDealFields {
  id: number;
  leadSourceId: number;
  merchantId: number | null;
  merchant: string;
  rep: string;
  funder: string;
  status: string;
  decision: Decision;
  amount: number;
  factorRate: number | null;
  term: number | null;
  declineReason: string;
  reason: string;
  date: string | null;
  timestamp: number;
  dateFunded: string | null;
  approvalDate: string | null;
  /** This submission is its merchant's canonical primary approval. */
  isPrimary: boolean;
  /** merchant.merchant_response, set from the Approvals tab's live dropdown (REG-740). */
  merchantResponse: string | null;
  merchantFirstName: string;
  merchantEmail: string;
}

const APPROVED_STATUSES = new Set(['approved', 'contracts out', 'contracts in']);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const AMOUNT_BUCKETS = [
  { value: 'all', label: 'Any amount', min: 0, max: Number.POSITIVE_INFINITY },
  { value: 'under25', label: '< $25k', min: 0, max: 25_000 },
  { value: '25to50', label: '$25k - $50k', min: 25_000, max: 50_000 },
  { value: '50to100', label: '$50k - $100k', min: 50_000, max: 100_000 },
  { value: 'over100', label: '> $100k', min: 100_000, max: Number.POSITIVE_INFINITY },
];
const STATUS_LABELS: Record<Decision, string> = { funded: 'Funded', approved: 'Approved', declined: 'Declined', no_response: 'No Response' };

function decisionFor(status: string): Decision {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'funded') return 'funded';
  if (APPROVED_STATUSES.has(normalized)) return 'approved';
  if (normalized === 'declined') return 'declined';
  return 'no_response';
}

async function fetchLeadSourceSubmissions(leadSourceId: number): Promise<SubmissionRecord[]> {
  const supabase = supabaseClient as any;
  const pageSize = 1000;
  const rows: any[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('lead_source_submission_activity_v')
      .select(`lead_source,submission,merchant,merchant_label,funder_label,status,amount,factor_rate,term,reason_for_decline,missing_documents,activity_date,date_funded,approval_date,is_primary_approval,merchant_response,merchant_first_name,merchant_email,${SHEET_DEAL_SELECT}`)
      .eq('lead_source', leadSourceId)
      .order('submission', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < pageSize) break;
  }
  return rows.flatMap((row): SubmissionRecord[] => {
    if (row.submission == null || row.lead_source == null) return [];
    const date = row.activity_date;
    const declineReason = row.reason_for_decline || '';
    return [{
      id: row.submission, leadSourceId: row.lead_source, merchantId: row.merchant,
      merchant: row.merchant_label || `Merchant #${row.merchant ?? '-'}`,
      rep: row.main_rep_label || 'Unassigned',
      funder: row.funder_label || 'Unassigned', status: row.status || 'No Response',
      decision: decisionFor(row.status || ''), amount: Number(row.amount) || 0,
      factorRate: row.factor_rate, term: row.term, declineReason,
      reason: declineReason || row.missing_documents || '', date,
      timestamp: date ? new Date(date).getTime() : 0,
      dateFunded: row.date_funded ?? null,
      approvalDate: row.approval_date ?? null,
      isPrimary: row.is_primary_approval === true,
      merchantResponse: row.merchant_response ?? null,
      merchantFirstName: row.merchant_first_name || '',
      merchantEmail: row.merchant_email || '',
      ...sheetDealFields(row),
    }];
  }).sort((a, b) => b.timestamp - a.timestamp);
}

function statsFor(submissions: SubmissionRecord[]) {
  const funded = submissions.filter((row) => row.decision === 'funded');
  const approved = submissions.filter((row) => row.decision === 'approved');
  const declined = submissions.filter((row) => row.decision === 'declined');
  const noResponse = submissions.filter((row) => row.decision === 'no_response');
  const approvedUnits = funded.length + approved.length;
  const decided = approvedUnits + declined.length;
  const approvalAmount = [...funded, ...approved].reduce((sum, row) => sum + row.amount, 0);
  return {
    total: submissions.length,
    funded: funded.length,
    approved: approved.length,
    approvedUnits,
    declined: declined.length,
    noResponse: noResponse.length,
    decided,
    fundedAmount: funded.reduce((sum, row) => sum + row.amount, 0),
    approvalAmount,
    averageDeal: approvedUnits ? Math.round(approvalAmount / approvedUnits) : 0,
    approvalRate: decided ? Math.round((approvedUnits / decided) * 100) : 0,
    fundedRate: approvedUnits ? Math.round((funded.length / approvedUnits) * 100) : 0,
    lastDate: submissions[0]?.date ?? null,
  };
}

function money(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function shortMoney(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return money(value);
}

export async function loadLeadSources(): Promise<LeadRow[]> {
  const supabase = supabaseClient as any;
  const [{ data, error }, fundedResult, leadsResult] = await Promise.all([
    supabase
      .from('lead_source_view')
      .select('id,source,is_active,gross_calc,number_of_deals_calc,per_deal_calc,cost,rev_share,real_net_calc,cost_percentage_calc,net_percentage_calc')
      .order('gross_calc', { ascending: false, nullsFirst: false }),
    // Funded dollar volume per source (REG-658); same attribution basis as
    // the gross/deal calc columns.
    supabase.from('lead_source_funded_summary_v').select('lead_source,funded_amount'),
    // REG-828: Total Leads / Total Unique Leads, the Lead Imports reports'
    // per-source counts.
    supabase.rpc('lead_source_lead_totals'),
  ]);
  if (error) throw error;
  if (fundedResult.error) throw fundedResult.error;
  if (leadsResult.error) throw leadsResult.error;
  const leadsBySource = new Map<number, { total: number; unique: number }>(
    (leadsResult.data || []).map((row: any) => [Number(row.lead_source), { total: Number(row.total_leads) || 0, unique: Number(row.unique_leads) || 0 }]),
  );
  // No rows at all means the Lead Imports reports were never refreshed:
  // show '-', not zero leads.
  const leadsKnown = leadsBySource.size > 0;
  const fundedBySource = new Map<number, number>(
    (fundedResult.data || []).map((row: any) => [Number(row.lead_source), Number(row.funded_amount) || 0]),
  );
  return (data || []).flatMap((row: any): LeadRow[] => row.id == null ? [] : [{
    id: row.id,
    source: row.source || `Lead #${row.id}`,
    gross: Number(row.gross_calc) || 0,
    fundedAmount: fundedBySource.get(Number(row.id)) || 0,
    deals: Number(row.number_of_deals_calc) || 0,
    perDeal: Number(row.per_deal_calc) || 0,
    cost: row.cost == null ? null : Number(row.cost),
    revShare: row.rev_share == null ? null : Number(row.rev_share),
    realNet: row.real_net_calc == null ? null : Number(row.real_net_calc),
    costPct: row.cost_percentage_calc == null ? null : Number(row.cost_percentage_calc),
    netPct: row.net_percentage_calc == null ? null : Number(row.net_percentage_calc),
    averageFunded: (Number(row.number_of_deals_calc) || 0) > 0
      ? Math.round((fundedBySource.get(Number(row.id)) || 0) / Number(row.number_of_deals_calc))
      : null,
    totalLeads: leadsKnown ? (leadsBySource.get(Number(row.id))?.total ?? 0) : null,
    uniqueLeads: leadsKnown ? (leadsBySource.get(Number(row.id))?.unique ?? 0) : null,
    active: row.is_active !== false,
    hasData: (Number(row.number_of_deals_calc) || 0) > 0,
  }]);
}

function Stat({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub: string; accent?: string }) {
  return <div className="rep-stat large"><span className="rep-stat-label">{label}</span><b style={{ color: accent }}>{value}</b><small>{sub}</small></div>;
}

function StatCard({ label, value, sub, accent, large }: { label: string; value: React.ReactNode; sub: string; accent?: string; large?: boolean }) {
  return <div className={`rep-stat ${large ? 'large' : ''}`}><span className="rep-stat-label">{label}</span><b style={{ color: accent }}>{value}</b><small>{sub}</small></div>;
}

function RateCard({ label, value, sub, accent }: { label: string; value: number; sub: string; accent: string }) {
  return <div className="rep-rate"><div className="rep-rate-ring" style={{ background: `conic-gradient(${accent} ${value * 3.6}deg, var(--border-soft) 0)` }}><span>{value}%</span></div><div><span className="rep-stat-label">{label}</span><small>{sub}</small></div></div>;
}

function Select({ value, onChange, children, label }: { value: string; onChange: (value: string) => void; children: React.ReactNode; label: string }) {
  return <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>;
}

function shortDate(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }).format(new Date(value));
}

const SUB_ID_COLUMN: ColDef<SubmissionRecord> = {
  headerName: 'SUB ID', colId: 'subId', field: 'id', width: 100, filter: 'agNumberColumnFilter',
  valueFormatter: (p) => (p.value != null ? `#${p.value}` : '-'),
};
const MAIN_REP_COLUMN: ColDef<SubmissionRecord> = { headerName: 'MAIN REP', field: 'rep', width: 150, tooltipField: 'rep' };
const MERCHANT_COLUMN: ColDef<SubmissionRecord> = { headerName: 'BUSINESS NAME', field: 'merchant', width: 200, cellClass: 'primary-cell', tooltipField: 'merchant' };
const STATUS_COLUMN: ColDef<SubmissionRecord> = { headerName: 'STATUS', colId: 'status', width: 150, filter: 'agSetColumnFilter',
  valueGetter: (p) => p.data ? STATUS_LABELS[p.data.decision] : '',
  cellRenderer: (p: { data?: SubmissionRecord }) => p.data ? <span className={`rep-status ${p.data.decision}`}><i />{STATUS_LABELS[p.data.decision]}</span> : null };
const REASON_COLUMN: ColDef<SubmissionRecord> = { headerName: 'DECLINE REASON / NOTE', field: 'reason', width: 240, tooltipField: 'reason',
  cellClass: (p) => (p.data?.decision === 'declined' ? 'decline-reason' : ''), valueFormatter: (p) => p.value || '-' };
const AMOUNT_COLUMN: ColDef<SubmissionRecord> = { headerName: 'AMOUNT', field: 'amount', type: 'rightAligned', width: 120, filter: 'agNumberColumnFilter', cellClass: 'numeric primary-cell', valueFormatter: (p) => money(p.value) };
const FACTOR_COLUMN: ColDef<SubmissionRecord> = { headerName: 'FACTOR', field: 'factorRate', type: 'rightAligned', width: 100, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(2) : '-') };
const TERM_COLUMN: ColDef<SubmissionRecord> = { headerName: 'TERM', field: 'term', type: 'rightAligned', width: 90, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? String(p.value) : '-') };
const FUNDER_COLUMN: ColDef<SubmissionRecord> = { headerName: 'FUNDER', field: 'funder', width: 170, tooltipField: 'funder' };
const hidden = (column: ColDef<SubmissionRecord>): ColDef<SubmissionRecord> => ({ ...column, hide: true });

// REG-828: the detail tabs follow the COLUMNS sheet of the matching list
// (SUBMISSIONS, FUNDED, APPROVALS). Lead Source is the page's own source, so
// it stays hidden; columns the sheet does not list stay hidden too.
const SHEET = sheetDealColumns<SubmissionRecord>();
const FIRST_NAME_COLUMN: ColDef<SubmissionRecord> = { headerName: 'FIRST NAME', field: 'merchantFirstName', width: 130, valueFormatter: (p) => p.value || '-' };
const EMAIL_COLUMN: ColDef<SubmissionRecord> = { headerName: 'EMAIL', field: 'merchantEmail', width: 200, valueFormatter: (p) => p.value || '-' };
const SUBMISSION_COLUMNS: ColDef<SubmissionRecord>[] = [
  SUB_ID_COLUMN,
  // REG-828: the Submissions list's Created At (long format); the old
  // activity date stays in the column chooser.
  SHEET.createdAt,
  MAIN_REP_COLUMN,
  MERCHANT_COLUMN,
  STATUS_COLUMN,
  REASON_COLUMN,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  SHEET.payment,
  TERM_COLUMN,
  SHEET.commissionPct,
  SHEET.commission,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(SHEET.leadSource),
  hidden(FUNDER_COLUMN),
  { headerName: 'ACTIVITY DATE', field: 'date', width: 110, valueFormatter: (p) => shortDate(p.value), hide: true },
];

const FUNDED_TAB_COLUMNS: ColDef<SubmissionRecord>[] = [
  SUB_ID_COLUMN,
  { headerName: 'DATE FUNDED', field: 'dateFunded', width: 120, valueFormatter: (p) => shortDate(p.value) },
  MAIN_REP_COLUMN,
  MERCHANT_COLUMN,
  SHEET.performance,
  FIRST_NAME_COLUMN,
  SHEET.phone,
  EMAIL_COLUMN,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  TERM_COLUMN,
  SHEET.payment,
  SHEET.commissionPct,
  SHEET.commission,
  SHEET.psf,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(SHEET.leadSource),
  hidden(FUNDER_COLUMN),
  hidden(STATUS_COLUMN),
  hidden(REASON_COLUMN),
];
const APPROVAL_TAB_COLUMNS: ColDef<SubmissionRecord>[] = [
  SUB_ID_COLUMN,
  { headerName: 'DATE APPROVED', field: 'approvalDate', width: 130, valueFormatter: (p) => shortDate(p.value) },
  MAIN_REP_COLUMN,
  MERCHANT_COLUMN,
  FIRST_NAME_COLUMN,
  SHEET.phone,
  EMAIL_COLUMN,
  SHEET.state,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  TERM_COLUMN,
  SHEET.payment,
  SHEET.commissionPct,
  SHEET.commission,
  // REG-740: live Merchant Feedback dropdown, same as the Approvals board.
  { headerName: 'MERCHANT FEEDBACK', colId: 'merchantResponse', field: 'merchantResponse', width: 170,
    filter: 'agSetColumnFilter', cellClass: 'live-picklist-cell',
    cellRenderer: (p: { data?: SubmissionRecord; value?: string | null }) => p.data ? (
      <LivePicklistCell
        target={MERCHANT_FEEDBACK_EDIT}
        recordId={p.data.merchantId}
        value={p.value ?? null}
        label="Merchant Feedback"
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['lead-source-submissions'] })}
      />
    ) : null },
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(SHEET.leadSource),
  hidden(FUNDER_COLUMN),
  hidden(STATUS_COLUMN),
  hidden(REASON_COLUMN),
];

type LeadDetailTab = 'funded' | 'submissions' | 'approvals' | 'applications';
const LEAD_DETAIL_TABS: Array<{ id: LeadDetailTab; label: string }> = [
  { id: 'funded', label: 'Funded' },
  { id: 'submissions', label: 'Submissions' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'applications', label: 'Applications' },
];

function SubmissionTable({ rows, onOpen, maxHeight, columns, storageKey }: { rows: SubmissionRecord[]; onOpen: (row: SubmissionRecord) => void; maxHeight: number; columns?: ColDef<SubmissionRecord>[]; storageKey?: string }) {
  return (
    <WorkspaceGrid<SubmissionRecord>
      rows={rows}
      columns={columns ?? SUBMISSION_COLUMNS}
      getRowId={(row) => String(row.id)}
      onRowClick={onOpen}
      height={maxHeight}
      storageKey={storageKey}
      emptyMessage="No submissions match these filters."
    />
  );
}

function LeadSourceDetail({ lead, submissions, onBack, onActiveChange }: { lead: LeadRow; submissions: SubmissionRecord[]; onBack: () => void;
  onActiveChange?: (active: boolean) => Promise<void> }) {
  const [savingActive, setSavingActive] = useState(false);
  const toggleActive = async (active: boolean) => {
    if (!onActiveChange || savingActive) return;
    setSavingActive(true);
    try {
      await onActiveChange(active);
      message.success(active ? `${lead.source} marked active.` : `${lead.source} marked inactive.`);
    } catch (error) {
      message.error((error as Error).message || 'Unable to update active status.');
    } finally {
      setSavingActive(false);
    }
  };
  const [status, setStatus] = useState('all');
  const [month, setMonth] = useState('all');
  const [year, setYear] = useState('all');
  const [amountBucket, setAmountBucket] = useState('all');
  const [reason, setReason] = useState('all');
  const [activeTab, setActiveTab] = useState<LeadDetailTab>('funded');
  // Dev-only Funded Dashboard for just this lead source (the same tables as Admin > Reports).
  const [showDashboard, setShowDashboard] = useState(false);
  const canSeeDashboard = useMyCapabilities().hasCapability('edit_permissions');
  const { openRecord } = useRecordNavigator();
  const years = [...new Set(submissions.filter((row) => row.date).map((row) => new Date(row.date!).getFullYear()))].sort((a, b) => b - a);
  const reasons = [...new Set(submissions.map((row) => row.declineReason).filter(Boolean))].sort();
  const bucket = AMOUNT_BUCKETS.find((item) => item.value === amountBucket) ?? AMOUNT_BUCKETS[0];
  const filtered = submissions.filter((row) => {
    const date = row.date ? new Date(row.date) : null;
    return (status === 'all' || row.decision === status)
      && (month === 'all' || date?.getMonth() === Number(month))
      && (year === 'all' || date?.getFullYear() === Number(year))
      && row.amount >= bucket.min && row.amount < bucket.max
      && (reason === 'all' || row.declineReason === reason);
  });
  const stats = statsFor(filtered);
  const allStats = statsFor(submissions);
  const merchantCount = new Set(submissions.map((row) => row.merchantId).filter(Boolean)).size;
  const repCount = new Set(submissions.map((row) => row.rep).filter((value) => value !== 'Unassigned')).size;
  const funderCount = new Set(submissions.map((row) => row.funder).filter((value) => value !== 'Unassigned')).size;
  const filteredView = status !== 'all' || month !== 'all' || year !== 'all' || amountBucket !== 'all' || reason !== 'all';

  // Tab row sets, each ordered most recent first on the tab's own date
  // (mirrors RepPage's detail tabs).
  const fundedRows = filtered.filter((row) => row.decision === 'funded')
    .sort((a, b) => new Date(b.dateFunded ?? b.date ?? 0).getTime() - new Date(a.dateFunded ?? a.date ?? 0).getTime());
  const approvalRows = filtered.filter((row) => row.isPrimary)
    .sort((a, b) => new Date(b.approvalDate ?? b.date ?? 0).getTime() - new Date(a.approvalDate ?? a.date ?? 0).getTime());
  // One row per merchant: the latest submission stands in for the application.
  const applicationRows = (() => {
    const seen = new Set<number | string>();
    return filtered.filter((row) => {
      const id = row.merchantId ?? `s${row.id}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  })();
  const tabRows: Record<LeadDetailTab, SubmissionRecord[]> = {
    funded: fundedRows,
    submissions: filtered,
    approvals: approvalRows,
    applications: applicationRows,
  };
  const tabColumns: Partial<Record<LeadDetailTab, ColDef<SubmissionRecord>[]>> = {
    funded: FUNDED_TAB_COLUMNS,
    approvals: APPROVAL_TAB_COLUMNS,
  };

  const openSubmission = (row: SubmissionRecord) => openRecord({ id: row.id, tableName: 'submission', label: row.merchant });
  const clearFilters = () => { setStatus('all'); setMonth('all'); setYear('all'); setAmountBucket('all'); setReason('all'); };

  return <div className="rep-detail" data-testid="record-detail">
    <button className="rep-back" onClick={onBack}>‹ <span>All lead sources</span></button>
    <header className="rep-detail-header">
      <span className="rep-detail-avatar">{lead.source.slice(0, 2).toUpperCase()}</span>
      <div className="rep-detail-title"><h1 className="funder-title">{lead.source}<LeadSourceEditButton source={lead} /></h1><p>Lead source</p></div>
      <div className="rep-header-chips">
        <div className="rep-active-chip">
          <small>ACTIVE</small>
          <label title={onActiveChange ? `Mark ${lead.source} ${lead.active ? 'inactive' : 'active'}` : 'Read-only'}>
            <input
              type="checkbox"
              checked={lead.active}
              disabled={!onActiveChange || savingActive}
              onChange={(event) => void toggleActive(event.target.checked)}
            />
            <b className={lead.active ? 'accent' : ''}>{savingActive ? 'Saving…' : lead.active ? 'Yes' : 'No'}</b>
          </label>
        </div>
        <div><small>GROSS COMM.</small><b>{shortMoney(lead.gross)}</b></div>
        <div><small>REAL NET</small><b className="accent">{lead.realNet != null ? shortMoney(lead.realNet) : '-'}</b></div>
        <div><small>COST</small><b>{lead.cost != null ? shortMoney(lead.cost) : '-'}</b></div>
        <div><small>REV SHARE</small><b>{lead.revShare != null ? shortMoney(lead.revShare) : '-'}</b></div>
        <div><small>MERCHANTS</small><b>{merchantCount}</b></div>
        <div><small>REPS</small><b>{repCount}</b></div>
        <div><small>FUNDERS USED</small><b>{funderCount}</b></div>
        <div><small>LAST SUBMISSION</small><b>{shortDate(allStats.lastDate)}</b></div>
      </div>
    </header>
    <div className="rep-filter-bar"><span className="rep-filter-label">☰ &nbsp; FILTERS</span>
      <Select label="Status" value={status} onChange={setStatus}><option value="all">All statuses</option><option value="funded">Funded</option><option value="approved">Approved</option><option value="declined">Declined</option><option value="no_response">No Response</option></Select>
      <Select label="Month" value={month} onChange={setMonth}><option value="all">All months</option>{MONTHS.map((label, index) => <option key={label} value={index}>{label}</option>)}</Select>
      <Select label="Year" value={year} onChange={setYear}><option value="all">All years</option>{years.map((value) => <option key={value}>{value}</option>)}</Select>
      <Select label="Amount" value={amountBucket} onChange={setAmountBucket}>{AMOUNT_BUCKETS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Select>
      <Select label="Decline reason" value={reason} onChange={setReason}><option value="all">Any decline reason</option>{reasons.map((value) => <option key={value}>{value}</option>)}</Select>
      {filteredView && <button onClick={clearFilters}>Clear</button>}<span className="filter-count">Showing <b>{stats.total}</b> of {submissions.length} submissions</span>
    </div>
    <div className="rep-stat-grid primary"><StatCard large label="Total Submissions" value={stats.total} sub={`${stats.decided} decided`} /><StatCard large label="Units Funded" value={stats.funded} sub={`of ${stats.approvedUnits} approved`} /><StatCard large label="Amount Funded" value={shortMoney(stats.fundedAmount)} sub={money(stats.fundedAmount)} accent="var(--green)" /><StatCard large label="Units Approved" value={stats.approvedUnits} sub={`${stats.approved} pending fund`} accent="var(--accent)" /><StatCard large label="Approval Amount" value={shortMoney(stats.approvalAmount)} sub={money(stats.approvalAmount)} accent="var(--accent)" /></div>
    <div className="rep-stat-grid secondary"><StatCard label="Declined Deals" value={stats.declined} sub="lost submissions" accent="var(--red)" /><StatCard label="No Response" value={stats.noResponse} sub="awaiting / stale" accent="var(--amber)" /><StatCard label="Avg Deal Size" value={shortMoney(stats.averageDeal)} sub="per approval" /><RateCard label="Approval Rate" value={stats.approvalRate} sub={`${stats.approvedUnits} / ${stats.decided} decided`} accent="var(--green)" /><RateCard label="Funded Rate" value={stats.fundedRate} sub={`${stats.funded} / ${stats.approvedUnits} approved`} accent="var(--accent)" /></div>
    <section className="rep-data-card">
      <div className="rep-tabs" role="tablist">
        {LEAD_DETAIL_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={!showDashboard && activeTab === tab.id}
            className={!showDashboard && activeTab === tab.id ? 'active' : ''}
            onClick={() => { setActiveTab(tab.id); setShowDashboard(false); }}
          >
            {tab.label} <em>{tabRows[tab.id].length}</em>
          </button>
        ))}
        {canSeeDashboard && <button role="tab" aria-selected={showDashboard} className={showDashboard ? 'active' : ''} onClick={() => setShowDashboard(true)}>Dashboard</button>}
        <span className="rep-tabs-note">{filteredView ? 'filtered view' : 'all time'}</span>
      </div>
      {/* One grid per tab, so each restores its own saved columns. */}
      {showDashboard ? <FundedDashboard embedded scope={{ kind: 'lead_source', id: lead.id }} /> : <SubmissionTable key={activeTab} storageKey={`lead_source_detail_${activeTab}`} rows={tabRows[activeTab]} onOpen={openSubmission} maxHeight={460} columns={tabColumns[activeTab]} />}
    </section>
    <LeadSourceImports source={lead} />
  </div>;
}

// REG-828: default columns and order from the COLUMNS sheet (LEAD SOURCE).
// Columns the sheet does not list stay available, hidden, in the column chooser.
const COLUMNS: ColDef<LeadRow>[] = [
  { headerName: 'LEAD SOURCE', field: 'source', flex: 130, minWidth: 150,
    cellRenderer: (p: { data?: LeadRow }) => p.data ? (
      <div className="funder-name"><span>{p.data.source.slice(0, 2).toUpperCase()}</span><b>{p.data.source}</b></div>
    ) : null },
  { headerName: 'TOTAL LEADS', field: 'totalLeads', type: 'rightAligned', flex: 110, minWidth: 120, cellClass: 'numeric', filter: 'agNumberColumnFilter',
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : '-') },
  { headerName: 'TOTAL UNIQUE LEADS', field: 'uniqueLeads', type: 'rightAligned', flex: 130, minWidth: 160, cellClass: 'numeric', filter: 'agNumberColumnFilter',
    valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : '-') },
  { headerName: 'TOTAL COST', field: 'cost', type: 'rightAligned', flex: 110, minWidth: 110, cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? money(p.value) : '—') },
  { headerName: 'TOTAL REV SHARE $', field: 'revShare', type: 'rightAligned', flex: 130, minWidth: 150, cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? money(p.value) : '—') },
  { headerName: 'TOTAL FUNDED UNITS', field: 'deals', type: 'rightAligned', flex: 120, minWidth: 160, cellClass: 'numeric', valueFormatter: (p) => (p.value ? String(p.value) : '—') },
  { headerName: 'TOTAL FUNDED AMOUNT', field: 'fundedAmount', type: 'rightAligned', flex: 140, minWidth: 170, cellClass: 'numeric funded-number', valueFormatter: (p) => (p.value ? money(p.value) : '—') },
  { headerName: 'TOTAL GROSS COMMISSION', field: 'gross', type: 'rightAligned', flex: 150, minWidth: 190, cellClass: 'numeric primary-cell', valueFormatter: (p) => money(p.value) },
  { headerName: 'REAL NET %', field: 'netPct', type: 'rightAligned', flex: 110, minWidth: 110, cellClass: 'numeric',
    valueFormatter: (p) => (p.value != null ? `${p.value.toFixed(1)}%` : '—'),
    cellRenderer: (p: { data?: LeadRow }) => p.data?.netPct == null ? '—'
      : <b style={{ color: p.data.netPct >= 0 ? 'var(--green)' : 'var(--red)' }}>{p.data.netPct.toFixed(1)}%</b> },
  { headerName: 'COST %', field: 'costPct', type: 'rightAligned', flex: 90, minWidth: 90, cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? `${p.value.toFixed(1)}%` : '—') },
  { headerName: 'AVERAGE FUNDED AMOUNT', field: 'averageFunded', type: 'rightAligned', flex: 150, minWidth: 190, cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? money(p.value) : '—') },
  { headerName: 'ACTIVE', colId: 'active', flex: 90, minWidth: 90, filter: 'agSetColumnFilter',
    valueGetter: (p) => (p.data ? (p.data.active ? 'Yes' : 'No') : '') },
  { headerName: 'PER DEAL', field: 'perDeal', type: 'rightAligned', flex: 100, minWidth: 95, cellClass: 'numeric', hide: true, valueFormatter: (p) => money(p.value) },
  { headerName: 'REAL NET', field: 'realNet', type: 'rightAligned', flex: 120, minWidth: 100, cellClass: 'numeric funded-number', hide: true, valueFormatter: (p) => (p.value != null ? money(p.value) : '—') },
];

export default function LeadSourcePage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [profit, setProfit] = useState('all');
  const { openRecord, closeRecord } = useRecordNavigator();
  const { data: metadata } = useGlobalMetadata();
  const selectedId = useRecordParam('lead_source');
  const leadSourcesQuery = useQuery({ queryKey: ['lead-source-workspace'], queryFn: loadLeadSources, staleTime: 5 * 60_000 });
  const { data, isLoading, error } = leadSourcesQuery;
  const selected = selectedId ? data?.find((row) => row.id === selectedId) : null;
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  // Mirrors the RLS gate: lead_source UPDATE requires the edit_deals capability.
  const canEditLeadSources = !capabilitiesLoading && hasCapability('edit_deals');
  const setLeadSourceActive = async (leadSourceId: number, active: boolean) => {
    // .select('id') probes the write: RLS filters silently, so an empty result
    // means the row was not actually updated (same pattern as funders).
    const { data: updated, error: updateError } = await (supabaseClient as any)
      .from('lead_source')
      .update({ is_active: active })
      .eq('id', leadSourceId)
      .select('id');
    if (updateError) throw updateError;
    if (!updated?.length) throw new Error('You may not have permission to change lead source status.');
    await leadSourcesQuery.refetch();
  };
  const detailQuery = useQuery({
    queryKey: ['lead-source-submissions', selectedId],
    queryFn: () => fetchLeadSourceSubmissions(selectedId!),
    enabled: selectedId != null,
    staleTime: 2 * 60_000,
  });

  // The source's fields edit in the record modal over its page; refresh the
  // header numbers once that modal closes.
  const location = useLocation();
  const hasOverlay = new URLSearchParams(location.search).has(OVERLAY_PARAM);
  const hadOverlay = useRef(hasOverlay);
  useEffect(() => {
    if (hadOverlay.current && !hasOverlay) void leadSourcesQuery.refetch();
    hadOverlay.current = hasOverlay;
  }, [hasOverlay, leadSourcesQuery]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data || []).filter((row) => {
      if (query && !row.source.toLowerCase().includes(query)) return false;
      if (profit === 'profitable') return row.hasData && (row.netPct ?? 0) >= 0;
      if (profit === 'unprofitable') return row.hasData && (row.netPct ?? 0) < 0;
      if (profit === 'nodata') return !row.hasData;
      return true;
    });
  }, [data, search, profit]);

  // Header gross/deals come from the canonical funded summary (REG-662) —
  // the per-source view counts funded deals without the clawback/date
  // filters, so its sums overstate the book. Cost and rev share stay
  // row-summed (they are per-source attributes).
  const fundedSummary = useFundedSummary().data;
  const totals = useMemo(() => {
    const source = data || [];
    return {
      gross: source.reduce((sum, row) => sum + row.gross, 0),
      realNet: source.reduce((sum, row) => sum + (row.realNet || 0), 0),
      deals: source.reduce((sum, row) => sum + row.deals, 0),
      cost: source.reduce((sum, row) => sum + (row.cost || 0), 0),
      revShare: source.reduce((sum, row) => sum + (row.revShare || 0), 0),
      sources: source.filter((row) => row.hasData).length,
    };
  }, [data]);

  const openLead = (row: LeadRow) => {
    const route = metadata?.routeData?.tableToRouteName?.lead_source || 'lead_source';
    openRecord({ id: row.id, table: route, tableName: 'lead_source', label: row.source });
  };

  if (error) {
    return <div className="rep-page-state"><strong>Unable to load lead sources</strong><span>{(error as Error).message}</span></div>;
  }
  if (selectedId != null) {
    if (isLoading || !data) {
      return <div className="rep-page-state"><span className="rep-spinner" /><span>Loading lead sources...</span></div>;
    }
    if (!selected) {
      return <div className="rep-page-state"><strong>Unable to load lead source</strong><span>This lead source no longer exists.</span></div>;
    }
    if (detailQuery.error) {
      return <div className="rep-page-state"><strong>Unable to load submissions</strong><span>{(detailQuery.error as Error).message}</span></div>;
    }
    if (detailQuery.isLoading || !detailQuery.data) {
      return <div className="rep-page-state"><span className="rep-spinner" /><span>Loading lead source activity...</span></div>;
    }
    return <LeadSourceDetail lead={selected} submissions={detailQuery.data} onBack={closeRecord}
      onActiveChange={canEditLeadSources ? (active) => setLeadSourceActive(selected.id, active) : undefined} />;
  }

  return (
    <div className="lead-page">
      <div className="rep-stat-grid primary">
        <Stat label="Gross Commission" value={fundedSummary ? shortMoney(fundedSummary.total_commission) : '—'} sub={fundedSummary ? money(fundedSummary.total_commission) : 'unique funded deals'} />
        <Stat label="Real Net" value={fundedSummary ? shortMoney(fundedSummary.total_commission - totals.cost - totals.revShare) : '—'} sub="gross − cost − rev share" accent="var(--green)" />
        <Stat label="Total Deals" value={fundedSummary ? fundedSummary.deals_funded.toLocaleString() : '—'} sub={`${totals.sources} sources`} accent="var(--accent)" />
        <Stat label="Marketing Cost" value={shortMoney(totals.cost)} sub={money(totals.cost)} accent="var(--red)" />
        <Stat label="Rev Share" value={shortMoney(totals.revShare)} sub="paid to partners" accent="var(--amber)" />
      </div>

      <ListLeaderboard
        title="Top Lead Sources"
        subtitle="by funded units · all time"
        entries={(data || []).map((row) => ({
          id: row.id,
          name: row.source,
          rank: row.deals,
          tieBreak: row.fundedAmount,
          stats: [
            { label: 'Units', value: String(row.deals) },
            { label: 'Funded', value: shortMoney(row.fundedAmount), accent: 'var(--green)' },
            { label: 'Gross', value: shortMoney(row.gross), accent: 'var(--accent)' },
            { label: 'Cost', value: row.cost != null ? shortMoney(row.cost) : '—', accent: 'var(--red)' },
          ],
        }))}
        onOpen={(id) => {
          const row = (data || []).find((entry) => entry.id === id);
          if (row) openLead(row);
        }}
      />

      <section className="funder-list-card">
        <div className="funder-toolbar">
            <label className="rep-search">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search lead sources..." />
            </label>
          <select aria-label="Profitability" value={profit} onChange={(event) => setProfit(event.target.value)}>
            <option value="all">All sources</option>
            <option value="profitable">Profitable (net +)</option>
            <option value="unprofitable">Unprofitable (net −)</option>
            <option value="nodata">No revenue yet</option>
          </select>
          <span><b>{rows.length}</b> sources</span>
          <CreateListRecordButton
            tableName="lead_source"
            onCreated={async (created) => {
              await leadSourcesQuery.refetch();
              const route = metadata?.routeData?.tableToRouteName?.lead_source || 'lead_source';
              openRecord({ id: created.id, table: route, tableName: 'lead_source', label: created.label });
            }}
          />
        </div>
        <WorkspaceGrid<LeadRow>
          exportName="Lead Sources"
          rows={rows}
          columns={COLUMNS}
          getRowId={(row) => String(row.id)}
          onRowClick={openLead}
          loading={isLoading}
          storageKey="lead_source_list"
          emptyMessage="No lead sources match."
        />
      </section>
    </div>
  );
}
